import { NavigateFunction } from "react-router-dom";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { ChargerRowTransactions, Row, RowTransactions, RowTeam } from "./types";
import React from "react";
import { Tag } from "antd";
import i18n from "i18next";
import { Transactions as TypeTransactionsPage } from "pages/transactions/Transactions";
import { Chargers } from "../Chargers";
import { ChargerTransactions } from "../Charger";
import { Button } from "antd";
import { useState } from "react";

type Sort = {
  sessions: boolean;
  energyInKWH: boolean;
  revenue: boolean;
};

type SortTransactions = {
  startTime: boolean;
  endTime: boolean;
  length: boolean;
  energy: boolean;
  revenue?: boolean;
};

type Transactions = {
  startTime: boolean;
  endTime: boolean;
  energy: boolean;
  revenue: boolean;
};

export const computeRowsAndCols = (
  data: Chargers,
  navigate: NavigateFunction,
  handleSort: (value: string) => void,
  shouldReset: (value: boolean) => void,
  reset: boolean,
  isAsc: Sort,
  isDesc: Sort,
  setIsAsc: (values: () => Sort) => void,
  setIsDesc: (values: () => Sort) => void
) => {
  if (!data.length) {
    return {
      columns: [],
      rows: [],
    };
  }

  const handleFilterChange = (field: string) => {
    if (!isAsc[field]) {
      setIsAsc(() => {
        const setSort = { sessions: false, energyInKWH: false, revenue: false };
        setSort[field] = true;
        return { ...setSort };
      });
      handleSort(field + "_asc");
      setIsDesc(() => {
        return { sessions: false, energyInKWH: false, revenue: false };
      });
    } else if (!isDesc[field] && isAsc[field]) {
      setIsAsc(() => {
        return { sessions: false, energyInKWH: false, revenue: false };
      });
      setIsDesc(() => {
        const setSort = { sessions: false, energyInKWH: false, revenue: false };
        setSort[field] = true;
        return { ...setSort };
      });
      handleSort(field + "_desc");
      shouldReset(false);
    }
  };

  const columns = [
    {
      title: (
        <div className="table-header">{i18n.t("chargers.stationName")}</div>
      ),
      dataIndex: "stationName",
      key: "stationName",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">{i18n.t("chargers.chargerName")}</div>
      ),
      dataIndex: "chargerName",
      key: "chargerName",
      align: "center" as const,
      render: (index) => {
        if (data[index]) {
          return (
            <a
              onClick={() =>
                navigate(`${encodeURIComponent(data[index].chargerId)}`, {
                  state: {
                    locationIdStationId: data[index]["locationId.stationId"],
                    status: data[index].status,
                  },
                })
              }
            >
              {data[index].chargerName}
            </a>
          );
        }
        return <a>{data[index].chargerName}</a>;
      },
    },
    {
      title: <div className="table-header">{i18n.t("chargers.assigned")}</div>,
      dataIndex: "assigned",
      key: "assigned",
      align: "center" as const,
    },
    {
      title: <div className="table-header">{i18n.t("chargers.status")}</div>,
      key: "status",
      dataIndex: "status",
      align: "center" as const,
      render: (tag) => {
        let color = "#CECECE";
        if (tag === "Fault") {
          color = "#FF7866";
        } else if (tag === "Available") {
          color = "#9DCC78";
        } else if (tag === "Unavailable") {
          color = "#FED077";
        } else if (tag.startsWith("In use")) {
          color = "#E088FF";
        }
        return (
          <Tag color={color} key={tag}>
            {tag.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: <div className="table-header">{i18n.t("chargers.type")}</div>,
      dataIndex: "type",
      key: "type",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">{i18n.t("chargers.priorityType")}</div>
      ),
      dataIndex: "priorityType",
      key: "priorityType",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">{i18n.t("chargers.accessType")}</div>
      ),
      dataIndex: "accessType",
      key: "accessType",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("chargers.sessions")}{" "}
          {reset ? (
            <CaretDownOutlined
              onClick={() => {
                handleFilterChange("sessions");
              }}
            />
          ) : !isDesc.sessions ? (
            <CaretDownOutlined
              onClick={() => {
                handleFilterChange("sessions");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleFilterChange("sessions");
              }}
            />
          )}
        </div>
      ),
      dataIndex: "sessions",
      key: "sessions",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("chargers.energy")}{" "}
          {reset ? (
            <CaretDownOutlined
              onClick={() => {
                handleFilterChange("energyInKWH");
              }}
            />
          ) : !isDesc.energyInKWH ? (
            <CaretDownOutlined
              onClick={() => {
                handleFilterChange("energyInKWH");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleFilterChange("energyInKWH");
              }}
            />
          )}
        </div>
      ),
      dataIndex: "energyInKWH",
      key: "energyInKWH",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("chargers.revenue")}{" "}
          {reset ? (
            <CaretDownOutlined
              onClick={() => {
                handleFilterChange("revenue");
              }}
            />
          ) : !isDesc.revenue ? (
            <CaretDownOutlined
              onClick={() => {
                handleFilterChange("revenue");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleFilterChange("revenue");
              }}
            />
          )}
        </div>
      ),
      dataIndex: "revenue",
      key: "revenue",
      align: "center" as const,
    },
    // {
    // 	title: <div className="table-header">{i18n.t("chargers.paymentStatus")}</div>,
    // 	dataIndex: "paymentStatus",
    // 	key: "paymentStatus",
    // 	align: "center" as const,
    // },
    // {
    // 	title: <div className="table-header">{i18n.t("chargers.pricingPlan")}</div>,
    // 	dataIndex: "pricingPlan",
    // 	key: "pricingPlan",
    // 	align: "center" as const,
    // },
  ];

  const rows: Row[] = [];
  Object.keys(data).forEach((key, index) => {
    const values = data[key];
    rows.push({
      key: (rows.length + 1).toString(),
      // chargerName: values.chargerName.toUpperCase() + "-" + (values.connectorId),
      chargerName: String(index),
      stationName: values.stationName,
      assigned: values.assigned,
      priorityType: values.priorityType,
      accessType: values.accessType,
      status: values.status,
      type: values.type,
      energyInKWH: values.energyInKWH,
      revenue: values.revenue,
      // pricingPlan: values.pricingPlan,
      // paymentStatus: values.paymentStatus,
      sessions: values.sessions,
    });
  });

  return {
    columns: columns,
    rows: rows,
  };
};

export const computeRowsAndColsForTransactions = (
  data: ChargerTransactions,
  handleSort: (value: string) => void,
  isAsc: SortTransactions,
  isDesc: SortTransactions,
  setIsAsc: (values: () => SortTransactions) => void,
  setIsDesc: (values: () => SortTransactions) => void,
  setCurrentTransaction: (value: any) => void,
  setIsModalVisible: (value: any) => void
) => {
  if (!data.length) {
    return {
      columns: [],
      rows: [],
    };
  }

  const handleSortChange = (field: string) => {
    if (!isAsc[field]) {
      setIsAsc(() => {
        const setSort = {
          startTime: false,
          endTime: false,
          length: false,
          energy: false,
        };
        setSort[field] = true;
        return { ...setSort };
      });
      handleSort(field + "_asc");
      setIsDesc(() => {
        return {
          startTime: false,
          endTime: false,
          length: false,
          energy: false,
        };
      });
    } else if (!isDesc[field] && isAsc[field]) {
      setIsAsc(() => {
        return {
          startTime: false,
          endTime: false,
          length: false,
          energy: false,
        };
      });
      setIsDesc(() => {
        const setSort = {
          startTime: false,
          endTime: false,
          length: false,
          energy: false,
        };
        setSort[field] = true;
        return { ...setSort };
      });
      handleSort(field + "_desc");
    }
  };

  const columns = [
    {
      title: (
        <div className="table-header">
          {i18n.t("transactions.transactionId")}
        </div>
      ),
      dataIndex: "transactionId",
      key: "transactionId",
      align: "center" as const,
      render: (ele, record) => (
        <a
          onClick={() => {
            // console.log(record);
            setCurrentTransaction(record);
            setIsModalVisible(true);
          }}
        >
          {record.transactionId}
        </a>
      ),
    },
    {
      title: <div className="table-header">{i18n.t("charger.userId")}</div>,
      dataIndex: "userId",
      key: "userId",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("charger.startTime")}
          {!isDesc.startTime ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("startTime");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("startTime");
              }}
            />
          )}
        </div>
      ),
      dataIndex: "startTime",
      key: "startTime",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("charger.endTime")}
          {!isDesc.endTime ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("endTime");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("endTime");
              }}
            />
          )}
        </div>
      ),
      key: "endTime",
      dataIndex: "endTime",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("charger.length")}
          {!isDesc.length ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("length");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("length");
              }}
            />
          )}
        </div>
      ),
      dataIndex: "length",
      key: "length",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("charger.energy")}
          {!isDesc.energy ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("energy");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("energy");
              }}
            />
          )}
        </div>
      ),
      dataIndex: "energy",
      key: "energy",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("charger.revenue")}
          {!isDesc.revenue ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("revenue");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("revenue");
              }}
            />
          )}
        </div>
      ),
      dataIndex: "revenue",
      key: "revenue",
      align: "center" as const,
    },
    {
      title: <div className="table-header">{i18n.t("charger.useCase")}</div>,
      dataIndex: "useCase",
      key: "useCase",
      align: "center" as const,
    },
  ];

  const rows: ChargerRowTransactions[] = [];
  Object.keys(data).forEach((key) => {
    const values = data[key];
    rows.push({
      key: (rows.length + 1).toString(),
      transactionId: values.transactionId,
      revenue: values.revenue,
      userId: values.userId,
      startTime: values.startTime,
      endTime: values.endTime,
      length: values.length.toString() + " " + values.lengthUnit,
      energy: values.energy.toString() + " " + values.energyUnit,
      useCase: values.useCase,
      sessionLength: values.sessionLength,
      chargingLength: values.chargingLength,
      startedBy: values.startedBy,
      stoppedBy: values.stoppedBy,
    });
  });

  return {
    columns: columns,
    rows: rows,
  };
};

export const computeRowsAndColsForTransactionsPage = (
  data: TypeTransactionsPage,
  navigate: NavigateFunction,
  handleSort: (value: string) => void,
  shouldReset: (value: boolean) => void,
  reset: boolean,
  isAsc: Transactions,
  isDesc: Transactions,
  setIsAsc: (values: () => Transactions) => void,
  setIsDesc: (values: () => Transactions) => void,
  setTransactions: (value: TypeTransactionsPage) => void,
  setReset: (value: boolean) => void,
  transactions: TypeTransactionsPage,
  setRefundParams: (value: any) => void,
  setIsModalOpen: (value: any) => void,
  setRefundMax: (value: any) => void,
  setCurrentTransaction: (value: any) => void,
  setIsModalVisible: (value: any) => void
) => {
  if (data.length === 0) {
    return {
      columns: [],
      rows: [],
    };
  }

  const handleSortChange = (field: string) => {
    if (!isAsc[field]) {
      setIsAsc(() => {
        const setSort = {
          startTime: false,
          endTime: false,
          energy: false,
          revenue: false,
        };
        setSort[field] = true;
        return { ...setSort };
      });
      handleSort(field + "_asc");
      setIsDesc(() => {
        return {
          startTime: false,
          endTime: false,
          energy: false,
          revenue: false,
        };
      });
    } else if (!isDesc[field] && isAsc[field]) {
      setIsAsc(() => {
        return {
          startTime: false,
          endTime: false,
          energy: false,
          revenue: false,
        };
      });
      setIsDesc(() => {
        const setSort = {
          startTime: false,
          endTime: false,
          energy: false,
          revenue: false,
        };
        setSort[field] = true;
        return { ...setSort };
      });
      handleSort(field + "_desc");
    }
    shouldReset(false);
  };
  const onMouseEnter = (transactionId, paymentStatus) => {
    let newItems = [...transactions];
    // paymentStatus == "success"
    if (paymentStatus == "succeeded") {
      newItems = newItems.map((item) => {
        if (item.transactionId === transactionId) {
          item.isFlag = true;
        } else {
          item.isFlag = false;
        }
        return item;
      });
      setTransactions(newItems);
    }
    // setReset(true);
  };

  const onMouseLeave = (transactionId) => {
    let newItems = [...transactions];
    newItems = newItems.map((item) => {
      item.isFlag = false;
      return item;
    });
    setTransactions(newItems);
  };

  const refundTap = (refundParams) => {
    setRefundParams(refundParams);
    setRefundMax(refundParams.maxRefund);
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: (
        <div className="table-header">
          {i18n.t("transactions.transactionId")}
        </div>
      ),
      dataIndex: "transactionId",
      key: "transactionId",
      align: "center" as const,
      render: (ele, record) => (
        <a
          onClick={() => {
            // console.log(record);
            setCurrentTransaction(record);
            setIsModalVisible(true);
          }}
        >
          {record.transactionId}
        </a>
      ),
    },
    {
      title: (
        <div className="table-header">{i18n.t("transactions.userId")}</div>
      ),
      dataIndex: "userId",
      key: "userId",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">{i18n.t("transactions.station")}</div>
      ),
      dataIndex: "station",
      key: "station",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">{i18n.t("transactions.chargerId")}</div>
      ),
      dataIndex: "chargerId",
      key: "chargerId",
      align: "center" as const,
    },
    {
      title: <div className="table-header">{i18n.t("transactions.type")}</div>,
      dataIndex: "type",
      key: "type",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("transactions.startTime")}{" "}
          {reset ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("startTime");
              }}
            />
          ) : !isDesc.startTime ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("startTime");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("startTime");
              }}
            />
          )}
        </div>
      ),
      dataIndex: "startTime",
      key: "startTime",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("transactions.endTime")}{" "}
          {reset ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("endTime");
              }}
            />
          ) : !isDesc.endTime ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("endTime");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("endTime");
              }}
            />
          )}
        </div>
      ),
      key: "endTime",
      dataIndex: "endTime",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">
          {i18n.t("transactions.energy")}{" "}
          {reset ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("energy");
              }}
            />
          ) : !isDesc.energy ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("energy");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("energy");
              }}
            />
          )}
        </div>
      ),
      key: "energy",
      dataIndex: "energy",
      align: "center" as const,
    },

    {
      title: (
        <div className="table-header">
          {i18n.t("transactions.revenue")}
          {!isDesc.revenue ? (
            <CaretDownOutlined
              onClick={() => {
                handleSortChange("revenue");
              }}
            />
          ) : (
            <CaretUpOutlined
              onClick={() => {
                handleSortChange("revenue");
              }}
            />
          )}
        </div>
      ),
      key: "revenue",
      dataIndex: "revenue",
      align: "center" as const,
    },
    {
      title: (
        <div className="table-header">{i18n.t("chargers.paymentStatus")}</div>
      ),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      align: "center" as const,
      render: (ele, record) => {
        return !record.isFlag ? (
          <div
            onMouseEnter={() =>
              onMouseEnter(record.transactionId, record.paymentStatus)
            }
          >
            {record.paymentStatus}
          </div>
        ) : (
          <Button
            onClick={() => refundTap(record)}
            onMouseLeave={() => onMouseLeave(record.transactionId)}
          >
            {record.paymentStatus}
          </Button>
        );
      },
    },
    // {
    // 	title: <div className="table-header">{i18n.t("transactions.pricingPlan")}</div>,
    // 	dataIndex: "pricingPlan",
    // 	key: "pricingPlan",
    // 	align: "center" as const,
    // },
    // {
    // 	title: <div className="table-header">{i18n.t("transactions.receipt")}</div>,
    // 	dataIndex: "receipt",
    // 	key: "receipt",
    // 	align: "center" as const,
    // 	render: (ele, record) => {
    // 		return (record.receipt != "N/A" ? <a href={record.receipt} target="_blank">Receipt</a> : <div>{record.receipt}</div>)
    // 	}
    // },
    {
      title: (
        <div className="table-header">{i18n.t("transactions.useCase")}</div>
      ),
      dataIndex: "useCase",
      key: "useCase",
      align: "center" as const,
    },
  ];

  const rows: RowTransactions[] = [];
  Object.keys(data).forEach((key) => {
    const values = data[key];
    rows.push({
      key: (rows.length + 1).toString(),
      transactionId: values.transactionId,
      userId: values.userId,
      station: values.station,
      chargerId: values.chargerId.toUpperCase(),
      type: values.type,
      startTime: values.startTime,
      endTime: values.endTime,
      energy: values.energy.toString() + " " + values.energyUnit,
      revenue: values.revenue,
      paymentStatus: values.paymentStatus,
      isFlag: values.isFlag,
      sessionLength: values.sessionLength,
      chargingLength: values.chargingLength,
      startedBy: values.startedBy,
      stoppedBy: values.stoppedBy,
      maxRefund: values.maxRefund,
      useCase: values.useCase,
    });
  });

  return {
    columns: columns,
    rows: rows,
  };
};

export const computeRowsAndColsForTeamPage = (
  data: TypeTransactionsPage,
  navigate: NavigateFunction,
  handleSort: (value: string) => void,
  shouldReset: (value: boolean) => void,
  reset: boolean,
  isAsc: Transactions,
  isDesc: Transactions,
  setIsAsc: (values: () => Transactions) => void,
  setIsDesc: (values: () => Transactions) => void,
  setTransactions: (value: TypeTransactionsPage) => void,
  setReset: (value: boolean) => void,
  transactions: TypeTransactionsPage,
  setRefundParams: (value: any) => void,
  setIsModalOpen: (value: any) => void,
  setRecord: (value: any) => void,
  setEditOpen: (value: any) => void,
  setDeleteModelOpen: (value: any) => void
) => {
  if (data.length === 0) {
    return {
      columns: [],
      rows: [],
    };
  }

  const handleSortChange = (field: string) => {
    if (!isAsc[field]) {
      setIsAsc(() => {
        const setSort = {
          startTime: false,
          endTime: false,
          energy: false,
          revenue: false,
        };
        setSort[field] = true;
        return { ...setSort };
      });
      handleSort(field + "_asc");
      setIsDesc(() => {
        return {
          startTime: false,
          endTime: false,
          energy: false,
          revenue: false,
        };
      });
    } else if (!isDesc[field] && isAsc[field]) {
      setIsAsc(() => {
        return {
          startTime: false,
          endTime: false,
          energy: false,
          revenue: false,
        };
      });
      setIsDesc(() => {
        const setSort = {
          startTime: false,
          endTime: false,
          energy: false,
          revenue: false,
        };
        setSort[field] = true;
        return { ...setSort };
      });
      handleSort(field + "_desc");
    }
    shouldReset(false);
  };

  const onUpdate = (record) => {
    setEditOpen(true);
    setRecord(record);
  };

  const onDelete = (record) => {
    setDeleteModelOpen(true);
    setRecord(record);
  };

  const columns = [
    {
      title: <div className="table-header">{i18n.t("teams.email")}</div>,
      dataIndex: "email",
      key: "email",
      align: "center" as const,
    },
    {
      title: <div className="table-header">{i18n.t("teams.name")}</div>,
      dataIndex: "name",
      key: "name",
      align: "center" as const,
    },
    {
      title: <div className="table-header">{i18n.t("teams.stations")}</div>,
      dataIndex: "stations",
      key: "stations",
      align: "center" as const,
      render: (ele, record) => {
        return (
          <div>
            {record.stationNames && Array.isArray(record.stationNames)
              ? record.stationNames.map((stationName, index) => {
                  return <div key={index}>{stationName}</div>;
                })
              : ele}
          </div>
        );
      },
    },
    {
      title: <div className="table-header">{i18n.t("teams.status")}</div>,
      dataIndex: "operatorStatus",
      key: "operatorStatus",
      align: "center" as const,
      render: (tag) => {
        let color = "#CECECE";
        if (tag === "Active") {
          color = "#9DCC78";
        } else if (tag === "Pending") {
          color = "#FED077";
        }
        return (
          <Tag color={color} key={tag}>
            {tag.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: <div className="table-header">{i18n.t("teams.edit")}</div>,
      dataIndex: "stations",
      key: "stations",
      align: "center" as const,
      render: (ele, record) => {
        return (
          <div>
            <Button type="primary" onClick={() => onUpdate(record)}>
              {i18n.t("teams.update")}
            </Button>
            <Button
              type="primary"
              danger
              style={{ marginLeft: "30px" }}
              onClick={() => onDelete(record)}
            >
              {i18n.t("teams.delete")}
            </Button>
          </div>
        );
      },
    },
  ];

  const rows: RowTeam[] = [];
  Object.keys(data).forEach((key) => {
    const values = data[key];
    rows.push({
      key: (rows.length + 1).toString(),
      email: values.email,
      operatorStatus: values.operatorStatus,
      stations: values.stations,
      stationNames: values.stationNames,
      name:
        values.firstName && values.lastName
          ? values.firstName + " " + values.lastName
          : "N/A",
      firstName: values.firstName,
      lastName: values.lastName,
    });
  });

  return {
    columns: columns,
    rows: rows,
  };
};

export const addZerosToStringNumber = (number: string) => {
  const arrNumber = number.split("");
  while (arrNumber.length < 4) {
    arrNumber.unshift("0");
  }
  return arrNumber.reduce((previous, current) => previous + current, "");
};
